// src/pages/campaign-console/LaunchConsole.jsx
// Master–detail "Launch Console" — the new Campaigns view.
// Drop-in for the old table. Presentational: pass real campaign rows + handlers.
//
//   <LaunchConsole
//     campaigns={filteredCampaigns}
//     onCreate={() => setShowCreateModal(true)}
//     onToggleAi={(c) => toggleAiOn(c.id, c.ai_on)}
//     onOpenSettings={(c) => setExpanded(c.id)}
//     getSetup={getSetup}            // optional override (web/voice rules)
//   />
//
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Plus, Check, Lock, ArrowRight, ChevronDown, Filter, Copy, Zap, MessageSquare, Pause, Sparkles, ShieldCheck,
  FileText, Download, Archive, ArchiveRestore, Globe, Palette, Inbox,
} from 'lucide-react';
import ReadinessRing from './ReadinessRing';
import { useAuth } from '../../context/AuthContext';
import {
  STEPS, CHANNEL, STATUS, getSetup as defaultGetSetup, stepsDone, deriveStatus,
} from './consoleData';

// Ring reflects AI on/off at a glance: green when the campaign's AI is live,
// muted gray when it's off. (status === 'live' ⇔ campaign.ai_on === true.)
const ringColorFor = (status, done) =>
  status === 'live' ? '#16a34a' : '#94a3b8';

// The setup steps a campaign SHOWS. Web chat (channel 'chat', from the tenant's widget
// slot) has no follow-ups, so that row is not shown for it. Display only: the readiness
// model (deriveCampaignSetup) still has all five keys and follow-ups is always true.
//
// Web also gets different HELP TEXT on three rows (left over from Phase 2, item 3.6 of
// the Phase 3 plan): web has no timezone/business-hours/phone step of its own and no
// tone-and-persona interview, it runs entirely off the campaign's talk track, so the
// SMS/voice wording ("Number, timezone, business hours & talk track", "Tone & persona",
// "Questions asked before handoff") is misleading there. For SMS/voice (c?.channel !==
// 'chat') this returns the exact same STEPS array as before this change — same reference,
// same strings — so those two channels render byte-identical to HEAD.
const WEB_STEP_HELP = {
  setup:         'Talk track & hot-lead email',
  behavior:      'Uses the talk track',
  qualification: 'Uses the talk track',
};
const visibleStepsFor = (c) => {
  if (c?.channel !== 'chat') return STEPS;
  return STEPS.filter(s => s.key !== 'followups')
    .map(s => (WEB_STEP_HELP[s.key] ? { ...s, help: WEB_STEP_HELP[s.key] } : s));
};

/* ---------------------------------------------------------------- Rail */
function Rail({ campaigns, rows, selectedId, onSelect, onCreate, sortKey, setSortKey, query, setQuery }) {
  const [open, setOpen] = useState(false);
  const sortOptions = [
    { k: 'name', label: 'Name (A–Z)' }, { k: 'readiness', label: 'Setup progress' },
    { k: 'status', label: 'Status' }, { k: 'updated', label: 'Recently updated' },
  ];
  const sortLabel = sortOptions.find(o => o.k === sortKey).label;
  return (
    <div className="w-[320px] flex-none bg-surfox-rail border-r border-surfox-border flex flex-col">
      <div className="px-5 pt-[22px] pb-3.5">
        <div className="flex items-center justify-between">
          <h1 className="m-0 text-[18px] font-semibold text-surfox-ink">Campaigns</h1>
          <button onClick={onCreate} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white bg-surfox-primary rounded-lg px-2.5 py-[7px] hover:brightness-110"><Plus size={14} /> New</button>
        </div>
        <div className="relative mt-3.5">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-surfox-faint"><Search size={14} /></span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" className="w-full pl-[31px] pr-2.5 py-2 text-[12.5px] border border-surfox-border rounded-lg bg-white text-surfox-ink outline-none focus:ring-2 focus:ring-surfox-primary/25" />
        </div>
        <div className="relative mt-2.5 flex items-center justify-between">
          <span className="font-mono text-[10.5px] text-surfox-faint tracking-wide uppercase">{rows.length} campaign{rows.length === 1 ? '' : 's'}</span>
          <button onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-surfox-muted bg-white border border-surfox-border rounded-lg px-2.5 py-1.5 hover:bg-surfox-line">
            <Filter size={13} className="text-surfox-faint" />{sortLabel}<ChevronDown size={13} className={`text-surfox-faint transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div className="absolute top-full right-0 mt-1.5 z-20 bg-white border border-surfox-border rounded-[10px] shadow-[0_10px_30px_rgba(18,30,50,.14)] p-1.5 min-w-[168px]">
              {sortOptions.map(o => (
                <button key={o.k} onClick={() => { setSortKey(o.k); setOpen(false); }} className={`flex items-center justify-between w-full text-left text-[12.5px] font-semibold rounded-[7px] px-2.5 py-2 ${o.k === sortKey ? 'text-surfox-primary bg-surfox-primarySoft' : 'text-surfox-ink hover:bg-surfox-line'}`}>{o.label}{o.k === sortKey && <Check size={14} />}</button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="px-3 flex-1 overflow-y-auto">
        {rows.length === 0 && (
          <div className="px-3 py-6 text-[12px] text-surfox-faint text-center">No campaigns match your search.</div>
        )}
        {rows.map(({ c, done, status, ringDone, ringTotal }) => {
          const sel = c.id === selectedId, st = STATUS[status], Chan = CHANNEL[c.channel] || CHANNEL.sms;
          return (
            <button key={c.id} onClick={() => onSelect(c.id)} className={`w-full text-left flex items-center gap-3 px-3 py-[11px] rounded-[10px] mb-0.5 border ${sel ? 'bg-white border-surfox-border shadow-[0_1px_2px_rgba(18,30,50,.05)]' : 'border-transparent hover:bg-[#f1f4f9]'}`}>
              <ReadinessRing done={ringDone} total={ringTotal} size={34} thick={3.5} color={ringColorFor(status, done)} label={false} />
              <span className="min-w-0 flex-1">
                <span className={`block text-[13px] font-semibold truncate ${sel ? 'text-surfox-ink' : 'text-[#2c3845]'}`}>{c.name}</span>
                <span className="flex items-center gap-1.5 mt-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /><span className="text-[11px] text-surfox-muted font-medium">{st.label}</span>
                  <span className="text-[11px] text-surfox-faint">· {Chan.label}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Detail */
function Detail({ campaign, setup, done, status, onToggleAi, onOpenSection, fetchAudience, onTestConversation, sandboxEnabled, fetchGateStatus, onBlockedActivate, dncEligible, onScrubDnc, onArchive, onRestore, archivedView, webChatSnippet }) {
  // In a prospect demo the Surf builder is replaced by the demo guide, so Recalibrate has nothing to open: say so.
  const { user: authUser } = useAuth() || {};
  const isProspectDemo = authUser?.is_prospect_demo === true;
  // Sandbox test chat is available once the campaign has an engagement brief
  // (its pillars). simulate-conversation 400s without ai_instruction_engagement.
  const canTest = !!campaign.ai_instruction_engagement;
  // Web chat campaign (the tenant's widget slot points at it). Everything web-only
  // below is gated on this; SMS and voice render exactly as before.
  const isWeb = campaign.channel === 'chat';
  // Steps shown (web has no Follow-ups row). `done` stays the five-step count used by
  // every launch check; these two only drive what the ring and the copy SAY.
  const visibleSteps = visibleStepsFor(campaign);
  const shownTotal = visibleSteps.length;
  const shownDone = visibleSteps.filter(s => !!setup[s.key]).length;
  const remaining = shownTotal - shownDone;
  const Chan = CHANNEL[campaign.channel] || CHANNEL.sms;

  // Short, human-friendly campaign ID for the header. The full UUID is only
  // needed for integrations — copy button still yields the complete value.
  const shortId = String(campaign.id).slice(0, 8);
  const [copied, setCopied] = useState(false);
  const copyId = () => {
    navigator.clipboard?.writeText(String(campaign.id)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  // Audience & schedule — real per-campaign figures, fetched on selection.
  const [audience, setAudience] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setAudience(null);
    if (fetchAudience) {
      Promise.resolve(fetchAudience(campaign)).then(a => { if (!cancelled) setAudience(a); }).catch(() => {});
    }
    return () => { cancelled = true; };
    // campaign.channel: the web chat slot can move while this campaign stays selected,
    // and web and SMS fetch different figures. For an SMS campaign it never changes.
  }, [campaign.id, campaign.channel, fetchAudience]);

  // Pre-launch conversation gate — the campaign must be tested (or waived by the
  // smart-skip comparison) before AI can go on. The server enforces this on
  // ai-toggle; this only mirrors it so the button doesn't lie. While the status is
  // still loading we do NOT lock: the server is the authority, and flashing a lock
  // on an already-cleared campaign reads as broken.
  const [gate, setGate] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setGate(null);
    if (fetchGateStatus) {
      Promise.resolve(fetchGateStatus(campaign)).then(g => { if (!cancelled) setGate(g); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [campaign.id, fetchGateStatus]);
  const gateBlocking = !!gate && !gate.cleared;
  // "Ready" means the five setup steps are done. While the test gate is still blocking, the panel below says
  // "One step left: test it", so the chip says "Ready to test" instead of contradicting it.
  const st = (status === 'ready' && gateBlocking) ? { ...STATUS.ready, label: 'Ready to test' } : STATUS[status];
  const gateLabel = gate
    ? `Test this campaign first — ${gate.count} of ${gate.required} conversations completed.`
    : '';
  // Web chat sends nothing, so it has no sending window, send rate or finish estimate.
  const audStats = isWeb ? [
    ['Leads in list', audience ? Number(audience.leads || 0).toLocaleString('en-US') : '—'],
  ] : [
    ['Leads in list', audience ? Number(audience.leads || 0).toLocaleString('en-US') : '—'],
    ['Sending window', audience?.sendingWindow || '—'],
    ['Send rate', audience?.sendRate || '—'],
    ['Est. to finish', audience?.eta || '—'],
  ];
  // Website card counts. null = the count failed, shown as a dash, never as 0.
  const fmtCount = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US'));
  const webStats = [
    ['Visitors who opened the chat', fmtCount(audience?.web?.visitors)],
    ['Contacts captured', fmtCount(audience?.web?.contacts)],
    ['Bookings', fmtCount(audience?.web?.bookings)],
  ];
  const [snippetCopied, setSnippetCopied] = useState(false);
  const copySnippet = () => {
    if (!webChatSnippet) return;
    navigator.clipboard?.writeText(webChatSnippet).then(() => {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 1500);
    }).catch(() => {});
  };

  // Send progress — how many leads have actually been texted, with delivery breakdown.
  const total = Number(audience?.leads || 0);
  const processed = Number(audience?.processed || 0);
  // Never read 100% while leads remain (1,959 of 1,965 rounds up to 100).
  const pct = total > 0 ? (processed >= total ? 100 : Math.min(99, Math.floor((processed / total) * 100))) : 0;
  const barColor = pct >= 80 ? 'bg-surfox-green' : pct >= 50 ? 'bg-surfox-cyan' : pct >= 20 ? 'bg-amber-500' : 'bg-orange-400';
  const sourceFiles = Array.isArray(audience?.sourceFiles) ? audience.sourceFiles : [];
  const progStats = [
    // Messages, not leads: a lead who got a follow-up counts twice here, so this can exceed the "texted" lead count above.
    ['Messages delivered', Number(audience?.delivered || 0)],
    ['Failed', Number(audience?.failed || 0)],
    ['Retries', Number(audience?.retries || 0)],
    ['Queued', Number(audience?.queued || 0)],
  ];
  return (
    <div className="flex-1 min-w-0 overflow-y-auto px-7 py-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="m-0 text-[21px] font-semibold tracking-[-.3px] text-surfox-ink truncate max-w-[420px]">{campaign.name}</h2>
            <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${st.text} ${st.soft} px-2.5 py-1 rounded-full whitespace-nowrap`}>
              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-2 text-[12.5px] text-surfox-muted">
            <span className="inline-flex items-center gap-1.5"><Chan.Icon size={13} /> {Chan.label} campaign</span>
            <button onClick={copyId} className="font-mono text-surfox-faint inline-flex items-center gap-1.5 hover:text-surfox-muted" title={copied ? 'Copied!' : `Copy campaign ID (${campaign.id})`}>{shortId} {copied ? <Check size={12} className="text-surfox-green" /> : <Copy size={12} />}</button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Recalibrate with Surf — opens the focused fix-this-talk-track flow.
              Only shown when a custom talk track exists to recalibrate. Surf
              re-drafts from plain-English feedback, runs the contradiction check,
              shows before/after, and only overwrites on an explicit yes (the prior
              version is stashed, so it is reversible). */}
          {campaign.custom_talk_track_id && (
            <button
              type="button"
              onClick={() => {
                if (isProspectDemo) {
                  window.dispatchEvent(new CustomEvent('reviewer-blocked', { detail: { message: 'Recalibrate with Surf does not work in a demo. In your own account, you tell Surf what feels off and it rewrites the campaign for you.' } }));
                  return;
                }
                window.dispatchEvent(new CustomEvent('surf:recalibrate', {
                  detail: { campaignId: campaign.id, campaignName: campaign.name },
                }));
              }}
              title="Fix or adjust this campaign's messaging with Surf"
              className="inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] font-semibold rounded-[10px] px-3.5 py-2 border text-surfox-primary bg-white border-surfox-primary/30 hover:bg-surfox-primarySoft"
            >
              <Sparkles size={14} /> Recalibrate with Surf
            </button>
          )}
          {/* The sandbox only ever simulates an SMS thread (replyPromptBuilder mirrors
              the SMS warming prompt), so it has nothing truthful to show for a web-chat
              campaign. Hidden for web only; SMS and voice are unchanged. */}
          {sandboxEnabled && !isWeb && (
            <button
              onClick={() => canTest && onTestConversation?.(campaign)}
              disabled={!canTest}
              title={canTest ? 'Chat with your AI as if you were a new lead' : 'Finish building your campaign pillars first'}
              className={`inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] font-semibold rounded-[10px] px-3.5 py-2 border ${canTest ? 'text-surfox-primary bg-surfox-primarySoft border-transparent hover:brightness-95' : 'text-surfox-faint bg-surfox-line border-surfox-border cursor-not-allowed'}`}
            >
              <MessageSquare size={14} /> Test conversation
            </button>
          )}
          {/* Retroactive DNC scrub for this campaign's already-uploaded leads. Only
              shown when the tenant is DNC-enabled (include_dnc + BA credentials), and
              never for web chat, which texts nobody and has no uploaded list. */}
          {dncEligible && !isWeb && (
            <button
              type="button"
              onClick={() => onScrubDnc?.(campaign)}
              title="Scrub this campaign's existing leads against DNC and known litigator lists"
              className="inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] font-semibold rounded-[10px] px-3.5 py-2 border text-surfox-primary bg-white border-surfox-primary/30 hover:bg-surfox-primarySoft"
            >
              <ShieldCheck size={14} /> Scrub for DNC
            </button>
          )}
          {/* Archive / Restore. Until 2026-09-10 the only archive control was a small
              button at the bottom of the Setup settings pop-up, which nobody found once
              this console replaced the old table. Neutral styling on purpose: archiving
              is reversible, and red reads as "delete". */}
          {archivedView ? (
            onRestore && (
              <button
                type="button"
                onClick={() => onRestore(campaign)}
                title="Put this campaign back in your active list. Its AI stays paused until you go live."
                className="inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] font-semibold rounded-[10px] px-3.5 py-2 border text-surfox-ink bg-white border-surfox-border hover:bg-surfox-line"
              >
                <ArchiveRestore size={14} /> Restore campaign
              </button>
            )
          ) : (
            onArchive && (
              <button
                type="button"
                onClick={() => onArchive(campaign)}
                title="Hide this campaign from your list and pause its AI. Nothing is deleted."
                className="inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] font-semibold rounded-[10px] px-3.5 py-2 border text-surfox-muted bg-white border-surfox-border hover:bg-surfox-line hover:text-surfox-ink"
              >
                <Archive size={14} /> Archive
              </button>
            )
          )}
        </div>
      </div>

      {/* launch readiness */}
      <div className="mt-5 bg-white border border-surfox-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-[22px] py-[18px] border-b border-surfox-line">
          <div className="flex items-center gap-4">
            <ReadinessRing done={shownDone} total={shownTotal} size={52} thick={5} color={done === 5 ? '#16a34a' : '#0891b2'} />
            <div>
              <div className="text-[16px] font-bold text-surfox-ink">{archivedView ? 'This campaign is archived' : campaign.ai_on ? 'This campaign is live' : done === 5 && gateBlocking ? 'One step left: test it' : done === 5 ? 'Ready to launch' : 'Almost ready to launch'}</div>
              <div className="text-[13px] text-surfox-muted mt-0.5">{archivedView ? 'Hidden from your active list and not texting anyone. Restore it to make changes or go live.' : campaign.ai_on ? (isWeb ? 'Your AI is answering visitors on your website.' : 'Your AI is texting these leads now.') : done === 5 && gateBlocking ? `Run ${gate.required - gate.count} more test conversation${gate.required - gate.count > 1 ? 's' : ''} to see how your AI handles real replies, then this campaign can go live.` : remaining === 0 ? 'Every section’s green — flip AI on whenever you’re ready.' : `${remaining} step${remaining > 1 ? 's' : ''} left before this campaign can go live.`}</div>
            </div>
          </div>
          {/* An archived campaign never offers Go live. The ai-toggle path would send a
              batch for it straight away, from a view whose whole point is "not in use". */}
          {archivedView ? (
            onRestore ? (
              <button onClick={() => onRestore(campaign)} className="inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-bold text-surfox-ink bg-white border border-surfox-border rounded-[10px] px-5 py-[11px] hover:bg-surfox-line"><ArchiveRestore size={15} /> Restore</button>
            ) : null
          ) : campaign.ai_on ? (
            <button onClick={() => onToggleAi?.(campaign)} title="Pause this campaign's AI" className="inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-bold text-surfox-amber bg-surfox-amberSoft border border-surfox-amber/30 rounded-[10px] px-5 py-[11px] hover:brightness-95 transition-colors"><Pause size={14} fill="currentColor" /> Pause AI</button>
          ) : done === 5 && gateBlocking ? (
            /* Setup is complete but the campaign has not been tested yet. The button
               STAYS LOCKED — it never activates. It is deliberately not `disabled`,
               because a greyed-out button that does nothing when clicked explains
               nothing: clicking it opens the modal that says what's needed and why.
               Same locked styling as the incomplete-setup button below. */
            <button onClick={() => onBlockedActivate?.(campaign)} title={gateLabel}
              className="inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-bold text-surfox-faint bg-surfox-line border border-surfox-border rounded-[10px] px-5 py-[11px] cursor-not-allowed"><Lock size={15} /> Go live</button>
          ) : done === 5 ? (
            <button onClick={() => onToggleAi?.(campaign)} className="inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-bold text-white rounded-[10px] px-5 py-[11px] bg-gradient-to-br from-surfox-cyan400 to-surfox-cyan shadow-[0_6px_16px_rgba(8,145,178,0.32)] motion-safe:animate-sfPulseBtn hover:brightness-105"><Zap size={15} fill="#fff" /> Go live</button>
          ) : (
            <button disabled className="inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-bold text-surfox-faint bg-surfox-line border border-surfox-border rounded-[10px] px-5 py-[11px] cursor-not-allowed" title="Complete all setup steps to launch."><Lock size={15} /> Go live</button>
          )}
        </div>
        <div>
          {visibleSteps.map((s, i) => {
            const ok = !!setup[s.key];
            return (
              <div key={s.key} className={`flex items-center gap-3.5 px-[22px] py-3.5 ${i ? 'border-t border-surfox-line' : ''}`}>
                <span className={`w-[34px] h-[34px] rounded-[9px] flex items-center justify-center ${ok ? 'bg-surfox-greenSoft text-surfox-green' : 'bg-surfox-cyanSoft text-surfox-cyan'}`}>{ok ? <Check size={16} /> : <s.Icon size={16} />}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-surfox-ink">{s.label}</div>
                  <div className="text-[12px] text-surfox-faint mt-0.5">{s.help}</div>
                </div>
                {ok ? (
                  <button onClick={() => onOpenSection(s.key, true)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-surfox-green hover:underline"><Check size={14} /> Done</button>
                ) : (
                  <button onClick={() => onOpenSection(s.key, false)} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white bg-surfox-ink rounded-lg px-3 py-[7px] hover:brightness-125">Set up <ArrowRight size={13} /></button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Website — web chat only (2026-09-10). The install code comes from the page's
          existing GET /api/web-chat/widget call; appearance lives in Settings, Web Chat;
          conversations in the Web Chat inbox. Counts are cheap indexed head counts
          (fetchCampaignAudience). */}
      {isWeb && (
        <div className="bg-white border border-surfox-border rounded-[14px] px-[22px] py-4 mt-4">
          <div className="flex items-center justify-between gap-3 mb-3.5 flex-wrap">
            <div className="inline-flex items-center gap-2 text-[12.5px] font-bold text-surfox-muted"><Globe size={14} /> Website</div>
            <div className="flex items-center gap-4">
              <Link to="/settings?section=web-chat" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-surfox-primary hover:underline"><Palette size={13} /> Appearance in Settings</Link>
              <Link to="/web-chat" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-surfox-primary hover:underline"><Inbox size={13} /> Web Chat inbox</Link>
            </div>
          </div>
          {/* Phase 1e (Tom, 2026-09-10): the widget exists only while this campaign is live. */}
          <div className="text-[12px] text-surfox-faint mb-2">Paste this code into your website to show the chat. It shows only while this campaign is live: pause or archive it and the chat disappears.</div>
          {webChatSnippet ? (
            <div className="flex items-start gap-2">
              <code className="flex-1 min-w-0 font-mono text-[11.5px] text-surfox-ink bg-surfox-bg border border-surfox-line rounded-[10px] px-3 py-2.5 break-all">{webChatSnippet}</code>
              <button type="button" onClick={copySnippet} title="Copy the install code"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-semibold rounded-[10px] px-3 py-2 border text-surfox-primary bg-white border-surfox-primary/30 hover:bg-surfox-primarySoft flex-none">
                {snippetCopied ? <Check size={14} /> : <Copy size={14} />} {snippetCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          ) : (
            <div className="text-[12.5px] text-surfox-faint">Your install code is in Settings, Web Chat.</div>
          )}
          <div className="grid grid-cols-3 mt-4">
            {webStats.map(([k, v], i) => (
              <div key={k} className={`px-5 ${i ? 'border-l border-surfox-line' : ''}`}>
                <div className="text-[11.5px] text-surfox-faint font-semibold">{k}</div>
                <div className="font-mono text-[19px] font-semibold mt-1.5 leading-none text-surfox-ink">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* sending progress — how many leads have actually been texted. Not for web chat,
          which texts nobody. */}
      {!isWeb && (
      <div className="bg-white border border-surfox-border rounded-[14px] px-[22px] py-4 mt-4">
        <div className="flex items-center justify-between mb-3.5">
          <div className="text-[12.5px] font-bold text-surfox-muted">Sending progress</div>
          {total > 0 && (
            <div className="text-[12px] text-surfox-muted">
              <span className="font-mono font-semibold text-surfox-ink">{processed.toLocaleString('en-US')}</span>
              <span className="text-surfox-faint"> / {total.toLocaleString('en-US')} texted</span>
              <span className="text-surfox-faint"> · {pct}%</span>
            </div>
          )}
        </div>
        {total === 0 ? (
          <div className="text-[12.5px] text-surfox-faint">No leads in this campaign yet.</div>
        ) : (
          <>
            <div className="w-full bg-surfox-line rounded-full h-2 overflow-hidden">
              <div className={`h-2 rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="grid grid-cols-4 mt-4">
              {progStats.map(([k, v], i) => (
                <div key={k} className={`px-5 ${i ? 'border-l border-surfox-line' : ''}`}>
                  <div className="text-[11.5px] text-surfox-faint font-semibold">{k}</div>
                  <div className="font-mono text-[19px] font-semibold mt-1.5 leading-none text-surfox-ink">{v.toLocaleString('en-US')}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      )}

      {/* audience & schedule — real per-campaign figures (web: leads only) */}
      <div className="bg-white border border-surfox-border rounded-[14px] px-[22px] py-4 mt-4">
        <div className="text-[12.5px] font-bold text-surfox-muted mb-3.5">Audience &amp; schedule</div>
        <div className={isWeb ? 'grid grid-cols-1' : 'grid grid-cols-4'}>
          {audStats.map(([k, v], i) => (
            <div key={k} className={`px-5 ${i ? 'border-l border-surfox-line' : ''}`}>
              <div className="text-[11.5px] text-surfox-faint font-semibold">{k}</div>
              <div className="font-mono text-[19px] font-semibold mt-1.5 leading-none text-surfox-ink">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* source list — the file this campaign was imported from, so "did I upload
          the right list?" is answerable without asking support. Only bulk import
          leaves a file behind, so this is empty for web/voice and for campaigns
          filled by an integration or a single-entry add. */}
      {campaign.channel !== 'chat' && campaign.channel !== 'voice' && (
        <div className="bg-white border border-surfox-border rounded-[14px] px-[22px] py-4 mt-4">
          <div className="text-[12.5px] font-bold text-surfox-muted mb-3.5">Uploaded list</div>
          {sourceFiles.length === 0 ? (
            <div className="text-[12.5px] text-surfox-faint">
              No uploaded file for this campaign. Its leads were added another way, such as a single
              entry, a Surf setup, or an integration.
            </div>
          ) : (
            <div className="space-y-2.5">
              {sourceFiles.map((f) => {
                const kb = Number(f.file_size || 0) / 1024;
                const size = kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`;
                const when = new Date(f.created_at);
                return (
                  <div key={f.id} className="flex items-start justify-between gap-3 border border-surfox-line rounded-[10px] px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-surfox-faint flex-none" />
                        <span className="text-[13px] font-semibold text-surfox-ink truncate" title={f.file_name}>{f.file_name}</span>
                      </div>
                      <div className="text-[11.5px] text-surfox-faint mt-1">
                        Uploaded {when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {' at '}{when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {' · '}{size}
                        {f.status !== 'completed' ? ` · ${f.status}` : ''}
                      </div>
                      <div className="text-[11.5px] text-surfox-faint mt-0.5">
                        {Number(f.total_records || 0).toLocaleString('en-US')} rows in file
                        {' · '}{Number(f.successful_records || 0).toLocaleString('en-US')} added
                        {f.duplicate_records ? ` · ${Number(f.duplicate_records).toLocaleString('en-US')} already in your account` : ''}
                        {f.failed_records ? ` · ${Number(f.failed_records).toLocaleString('en-US')} skipped` : ''}
                      </div>
                    </div>
                    {f.file_url && (
                      <a
                        href={`${f.file_url}${f.file_url.includes('?') ? '&' : '?'}download=${encodeURIComponent(f.file_name || 'list.csv')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Download the file exactly as it was uploaded"
                        className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-semibold rounded-[10px] px-3 py-2 border text-surfox-primary bg-white border-surfox-primary/30 hover:bg-surfox-primarySoft flex-none"
                      >
                        <Download size={14} /> Download
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Page */
export default function LaunchConsole({
  campaigns = [],
  onCreate, onToggleAi,
  onOpenStep,     // (campaign, stepKey) -> open the full settings panel on that tab
  fetchAudience,  // async (campaign) -> { leads, sendingWindow, sendRate, eta }
  onTestConversation, // (campaign) -> open the sandbox test-chat modal
  sandboxEnabled = true, // the is_demo rollout gate was dropped 2026-07-15; sandbox is open to all
  fetchGateStatus, // async (campaign) -> { required, count, cleared, skipped } | null
  onBlockedActivate, // (campaign) -> explain why the gate is holding this campaign back
  dncEligible = false, // tenant has include_dnc + BA credentials -> show Scrub for DNC
  onScrubDnc, // (campaign) -> open the retroactive DNC scrub modal
  onArchive,  // (campaign) -> archive it (hides it and pauses its AI)
  onRestore,  // (campaign) -> restore an archived campaign (AI stays paused)
  archivedView = false, // the page is showing archived campaigns
  webChatSnippet = '', // the website install code, shown on the web chat campaign's Website card
  getSetup = defaultGetSetup,
  initialSelectedId,
}) {
  // Open on a texting (SMS) campaign unless a specific one was asked for: it is the channel with the Test conversation and the
  // clearest place to start. Falls back to the first campaign when there is no SMS one.
  const [selectedId, setSelectedId] = useState(initialSelectedId || (campaigns.find((c) => c.channel === 'sms') || campaigns[0])?.id);
  const [sortKey, setSortKey] = useState('name');
  const [query, setQuery] = useState('');

  // Precompute setup/status per campaign, then sort.
  // ringDone/ringTotal are what the rings DISPLAY: a web chat campaign has no Follow-ups
  // step (web never follows up), so it shows N/4. `done` still counts all five steps
  // (follow-ups is always true), so every "done === 5" launch check is unchanged and
  // 5/5 still means Go live passes. SMS and voice: ringDone === done, ringTotal === 5.
  const enriched = useMemo(() => campaigns.map(c => {
    const setup = getSetup(c);
    const done = stepsDone(setup);
    const status = c.status || deriveStatus(c, done === 5);
    const steps = visibleStepsFor(c);
    return { c, setup, done, status, ringDone: steps.filter(s => !!setup[s.key]).length, ringTotal: steps.length };
  }), [campaigns, getSetup]);

  const rows = useMemo(() => {
    const order = { live: 0, ready: 1, setup: 2, paused: 3 };
    const q = query.trim().toLowerCase();
    let a = [...enriched];
    if (q) a = a.filter(e => (e.c.name || '').toLowerCase().includes(q));
    if (sortKey === 'name') a.sort((x, y) => x.c.name.localeCompare(y.c.name));
    else if (sortKey === 'readiness') a.sort((x, y) => y.done - x.done || x.c.name.localeCompare(y.c.name));
    else if (sortKey === 'status') a.sort((x, y) => order[x.status] - order[y.status] || x.c.name.localeCompare(y.c.name));
    return a; // 'updated' = source order
  }, [enriched, sortKey, query]);

  const sel = enriched.find(e => e.c.id === selectedId) || enriched[0];
  if (!sel) {
    return <div className="h-full flex items-center justify-center text-surfox-faint font-sans">No campaigns yet.</div>;
  }

  return (
    <div className="relative flex h-full bg-surfox-bg font-sans text-surfox-ink antialiased rounded-2xl overflow-hidden border border-surfox-border">
      <Rail campaigns={campaigns} rows={rows} selectedId={sel.c.id} onSelect={setSelectedId} onCreate={onCreate} sortKey={sortKey} setSortKey={setSortKey} query={query} setQuery={setQuery} />
      <Detail
        campaign={sel.c} setup={sel.setup} done={sel.done} status={sel.status}
        onToggleAi={onToggleAi}
        fetchAudience={fetchAudience}
        onTestConversation={onTestConversation}
        sandboxEnabled={sandboxEnabled}
        fetchGateStatus={fetchGateStatus}
        onBlockedActivate={onBlockedActivate}
        dncEligible={dncEligible}
        onScrubDnc={onScrubDnc}
        onArchive={onArchive}
        onRestore={onRestore}
        archivedView={archivedView}
        webChatSnippet={webChatSnippet}
        // Each step IS a Settings tab — open the real panel on that tab.
        onOpenSection={(key) => onOpenStep?.(sel.c, key)}
      />
    </div>
  );
}
